import { unfoldSigtest } from '@aytm/app/stats/lib/query/sigtest-codec'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const round = (value, decimals) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

export const calcHcAnswers = ({ viewMode, answer, batches, topsMode, degree }) => {
  const hcAnswers = {}
  const confidenceLevels = PageGlobals.CONFIDENCE_LEVELS || []

  // Check if any batch has different total than main total
  const sqExist = answer?.total > 0 && batches.some(b => answer.total - (answer[`b${b.position}`]?.total || 0) > 0)

  let min = 0
  let max = 0

  for (const [bi, b] of batches.entries()) {
    const bKey = `b${b.position}`
    const fakeKey = `a${b.position}`
    const answerData = answer?.[bKey] || {}

    hcAnswers[fakeKey] = {}

    if (viewMode === 'meanScore') {
      hcAnswers[fakeKey].amount = answerData.mean?.value || 0
      hcAnswers[fakeKey].total = answerData.total || answer?.total
      hcAnswers[fakeKey].unitLabel = 'mean'
      hcAnswers[fakeKey].sq = sqExist ? answerData.total || 0 : 0

      // Error bars for confidence intervals
      for (const err of ['errLO', 'errHI']) {
        if (!answerData.mean?.[err]) continue

        hcAnswers[fakeKey][err] = Array.from({ length: confidenceLevels.length }, () => [0])
        for (let cli = 0; cli < confidenceLevels.length; cli++) {
          hcAnswers[fakeKey][err][cli] = [Math.abs(hcAnswers[fakeKey].amount - answerData.mean?.[err]?.[cli]) || 0]
        }
      }

      hcAnswers[fakeKey].sd = answerData.mean?.sd || 0
      hcAnswers[fakeKey].mu = answerData.mean?.value || 0
    } else if (viewMode === 'boxScore') {
      const boxData = answerData.box?.[topsMode] || {}
      hcAnswers[fakeKey].amount = boxData.amount || 0
      hcAnswers[fakeKey].percents = boxData.percents || 0
      hcAnswers[fakeKey].total = answerData.total || answer?.total
      hcAnswers[fakeKey].unitLabel = hcAnswers[fakeKey].amount === 1 ? 'response' : 'responses'

      // Error bars
      for (const err of ['errLO', 'errHI']) {
        if (!boxData[err]) continue

        hcAnswers[fakeKey][err] = Array.from({ length: confidenceLevels.length }, () => [0])
        for (let cli = 0; cli < confidenceLevels.length; cli++) {
          const errData = boxData[err]?.[cli] || []
          hcAnswers[fakeKey][err][cli] = [errData[0] || 0, errData[1] || 0]
        }
      }
    } else {
      // 'score' viewMode
      if (Object.hasOwn(answerData, 'scale')) {
        hcAnswers[fakeKey].amount = answerData.scale.amount
      } else {
        hcAnswers[fakeKey].amount = answerData.total ? answerData.score / answerData.total : 0
      }
      hcAnswers[fakeKey].unitLabel = 'score'
      hcAnswers[fakeKey].total = answerData.total || answer?.total

      for (const err of ['errLO', 'errHI']) {
        if (!answerData[err]) continue

        hcAnswers[fakeKey][err] = Array.from({ length: confidenceLevels.length }, () => [0])
        for (let cli = 0; cli < confidenceLevels.length; cli++) {
          hcAnswers[fakeKey][err][cli] = [answerData[err]?.[cli]?.[0] || 0]
        }
      }

      min = Math.min(min, hcAnswers[fakeKey].amount)
      max = Math.max(max, hcAnswers[fakeKey].amount)
    }

    // The batch's own count: `total` falls back to the question's so the tooltip has a base, and so
    // cannot tell a batch nobody answered
    hcAnswers[fakeKey].responses = answerData.total || 0

    // Items get sorted downstream; sigtest entries reference the original position
    hcAnswers[fakeKey].sigtestIndex = bi

    // Decode the v2 transport form to the legacy alpha-keyed dict (no-op if already unfolded)
    if (answerData.sigtest != null) hcAnswers[fakeKey].sigtest = unfoldSigtest(answerData.sigtest)
  }

  // Calculate yAxisLimits
  let yAxisLimits = null

  if (!['meanScore', 'boxScore'].includes(viewMode)) {
    min = min < 0 ? -Math.ceil(Math.abs(min)) : 0
    max = max <= 0 && min >= 0 ? 100 : max <= 0 ? Math.ceil(Math.abs(min)) : Math.ceil(max)
  }

  const hasNegativeScores = answer?.hasNegativeScores

  if (viewMode === 'score' && degree && !hasNegativeScores) {
    yAxisLimits = [0, degree]
  } else if (viewMode === 'meanScore' && degree) {
    yAxisLimits = [1, degree]
  } else if (viewMode === 'boxScore') {
    yAxisLimits = [0, 100]
  } else if (min !== max) {
    yAxisLimits = [round(min, 2), round(max, 2)]
  }

  return { hcAnswers, yAxisLimits }
}
